import { Alert, AlertDescription, AlertTitle } from "@car/ui/components/alert";
import { Badge } from "@car/ui/components/badge";
import { Button } from "@car/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@car/ui/components/card";
import { Progress } from "@car/ui/components/progress";
import { Skeleton } from "@car/ui/components/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@car/ui/components/table";
import { CreditCardIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { PageHeader, PanelEmpty, PanelQueryError } from "@/components/panel-bits";
import { usePaymentsOf } from "@/components/target-payments";
import { faDate, faNumber, faPercent, faToman, toFaDigits } from "@/lib/format";
import type { InstallmentRow } from "@/lib/panel";
import { PayDialog } from "@/routes/pay/-pay-dialog";
import { client, orpc } from "@/utils/orpc";

import { SEP } from "@car/ui/lib/utils";

export const Route = createFileRoute("/panel/installments")({
  head: () => ({ meta: [{ title: "اقساط و تسهیلات | پنل مشتریان تابان خودرو" }] }),
  component: InstallmentsPage,
});

const rowBadge = (row: InstallmentRow) => {
  if (row.status === "paid")
    return (
      <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">پرداخت شده</Badge>
    );
  if (row.status === "overdue") return <Badge variant="destructive">معوق</Badge>;
  if (row.status === "due")
    return (
      <Badge className="bg-amber-500/15 text-amber-800 dark:text-amber-400">سررسید بعدی</Badge>
    );
  return <Badge variant="outline">آینده</Badge>;
};

type Plan = Awaited<ReturnType<typeof client.panel.installments.list>>["plans"][number];
type LateFee = Awaited<ReturnType<typeof client.panel.installments.list>>["lateFee"];

/** ردیف‌های جدول پیش از «نمایش همه»: دو قسط پرداخت‌شده آخر و ده قسط بعدی */
const FIRST_ROWS = 12;

function InstallmentsPage() {
  const query = useQuery(orpc.panel.installments.list.queryOptions());

  return (
    <div>
      <PageHeader
        title="اقساط و تسهیلات"
        description="وضعیت تسهیلات خرید، جدول کامل سررسیدها، رسید هر قسط و پرداخت قسط بعدی."
      />

      {query.isPending ? (
        <div className="grid gap-4">
          <Skeleton className="h-40 rounded-xl" />
          <Skeleton className="h-72 rounded-xl" />
        </div>
      ) : query.isError ? (
        <PanelQueryError title="اقساط بارگیری نشد" query={query} />
      ) : query.data.plans.length === 0 ? (
        <PanelEmpty
          icon={CreditCardIcon}
          title="تسهیلات فعالی ندارید"
          description="اگر خودرو را اقساطی خریداری کنید، جدول اقساط اینجا نمایش داده می‌شود."
        />
      ) : (
        <div className="grid gap-6">
          {query.data.plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} lateFee={query.data.lateFee} />
          ))}
        </div>
      )}
    </div>
  );
}

function PlanCard({ plan, lateFee }: { plan: Plan; lateFee: LateFee }) {
  const [showAll, setShowAll] = useState(false);
  const payments = usePaymentsOf("installment", plan.id);
  const dueRow = plan.rows.find((row) => row.status === "due") ?? null;
  const overdue = plan.rows.filter((row) => row.status === "overdue");
  const feeTotal = overdue.reduce((sum, row) => sum + row.lateFee, 0);
  const remainingTotal = (plan.totalMonths - plan.paidMonths) * plan.monthlyAmount;
  const next = overdue[0] ?? dueRow;
  // دو قسط آخرِ پرداخت‌شده و قسط‌های بعدی؛ «نمایش همه» کل جدول را باز می‌کند
  const firstIndex = Math.max(0, plan.paidMonths - 2);
  const rows = showAll ? plan.rows : plan.rows.slice(firstIndex, firstIndex + FIRST_ROWS);
  const receiptsOf = (row: InstallmentRow) =>
    payments.filter((item) => item.installmentNo === row.index);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <HugeiconsIcon icon={CreditCardIcon} strokeWidth={1.8} className="size-5" />
            {plan.title}
          </CardTitle>
          <span className="text-sm text-muted-foreground">
            {plan.vehicleName}
            {plan.orderNumber ? (
              <>
                {plan.vehicleName ? SEP : null}
                <Link to="/panel/orders" hash={`order-${plan.orderId}`} className="underline">
                  سفارش {plan.orderNumber}
                </Link>
              </>
            ) : null}
          </span>
        </div>
      </CardHeader>
      <CardContent className="grid gap-4">
        {overdue[0] ? (
          <Alert variant="destructive">
            <AlertTitle>
              {faNumber(overdue.length)} قسط معوق به مبلغ{" "}
              {faToman(overdue.length * plan.monthlyAmount + feeTotal)}
            </AlertTitle>
            <AlertDescription>
              سررسید قسط {faNumber(overdue[0].index)} ({faDate(overdue[0].dueIso)}) گذشته است
              {feeTotal > 0 ? `؛ ${faToman(feeTotal)} جریمه دیرکرد تا امروز` : ""}. هر چه زودتر
              پرداخت کنید.
            </AlertDescription>
          </Alert>
        ) : null}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="rounded-lg bg-muted/60 p-3">
            <div className="text-xs text-muted-foreground">قسط ماهانه</div>
            <div className="mt-1 font-black">{faToman(plan.monthlyAmount)}</div>
          </div>
          <div
            className={
              overdue[0]
                ? "rounded-lg bg-destructive/10 p-3 text-destructive"
                : "rounded-lg bg-muted/60 p-3"
            }
          >
            <div className={overdue[0] ? "text-xs" : "text-xs text-muted-foreground"}>
              {overdue[0] ? "قسط بعدی (معوق)" : "قسط بعدی"}
            </div>
            <div className="mt-1 font-black">{next ? faDate(next.dueIso) : "تسویه شده"}</div>
          </div>
          <div className="rounded-lg bg-muted/60 p-3">
            <div className="text-xs text-muted-foreground">مانده کل تسهیلات</div>
            <div className="mt-1 font-black">{faToman(remainingTotal)}</div>
          </div>
        </div>

        <div className="grid gap-1.5">
          <Progress
            value={Math.round((plan.paidMonths / plan.totalMonths) * 100)}
            aria-label="پیشرفت پرداخت اقساط"
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm text-muted-foreground">
              {faNumber(plan.paidMonths)} قسط از {faNumber(plan.totalMonths)} قسط پرداخت شده است.
            </span>
            {next ? (
              <PayDialog
                purpose="installment"
                targetId={plan.id}
                title={`قسط ${faNumber(next.index)} ${plan.title}`}
              >
                پرداخت قسط {faNumber(next.index)}
              </PayDialog>
            ) : null}
          </div>
        </div>

        {plan.loan ? (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border p-3 sm:grid-cols-3">
            {[
              ["مبلغ وام", faToman(plan.loan.principal)],
              ["پیش‌پرداخت", faToman(plan.loan.downPayment)],
              ["سود سالانه", faPercent(plan.loan.annualRate)],
              ["کل سود", faToman(plan.loan.totalInterest)],
              ["جمع بازپرداخت", faToman(plan.loan.totalPayable)],
              ["مانده اصل وام", faToman(plan.loan.outstanding)],
            ].map(([label, value]) => (
              <div key={label}>
                <dt className="text-xs text-muted-foreground">{label}</dt>
                <dd className="mt-1 text-sm font-bold">{value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>قسط</TableHead>
                <TableHead>سررسید</TableHead>
                <TableHead>مبلغ</TableHead>
                <TableHead>وضعیت</TableHead>
                <TableHead>پرداخت</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.index}>
                  <TableCell className="font-bold">قسط {faNumber(row.index)}</TableCell>
                  <TableCell className="text-muted-foreground">{faDate(row.dueIso)}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {faToman(row.amount)}
                    {row.lateFee > 0 ? (
                      <span className="block text-xs text-destructive">
                        + {faToman(row.lateFee)} جریمه ({faNumber(row.daysLate)} روز)
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell>{rowBadge(row)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {receiptsOf(row).map((item) => (
                      <Link
                        key={item.id}
                        to="/pay/callback/$paymentId"
                        params={{ paymentId: item.id }}
                        search={{}}
                        className="block underline"
                      >
                        {item.status === "review" ? "در انتظار تأیید" : faDate(item.paidIso)}
                        {SEP}
                        {item.providerLabel}
                        {item.chequeNumber ? ` ${toFaDigits(item.chequeNumber)}` : ""}
                        {item.sandbox ? `${SEP}آزمایشی` : ""}
                      </Link>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {plan.rows.length > FIRST_ROWS ? (
          <Button
            variant="outline"
            size="sm"
            className="justify-self-start"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? "نمایش خلاصه جدول" : `نمایش همه ${faNumber(plan.rows.length)} قسط`}
          </Button>
        ) : null}
        <p className="text-xs text-muted-foreground">
          قسط را همین‌جا آنلاین یا کارت‌به‌کارت، یا در شعبه (نقد، کارت‌خوان یا چک) بپردازید؛ رسید هر قسط
          در ستون «پرداخت» می‌ماند.{" "}
          {lateFee.percentPerMonth > 0
            ? `جریمه دیرکرد: ${faPercent(lateFee.percentPerMonth)} مبلغ قسط برای هر ماه تأخیر، روزشمار${
                lateFee.graceDays > 0 ? `، پس از ${faNumber(lateFee.graceDays)} روز مهلت` : ""
              }.`
            : "برای تأخیر جریمه‌ای محاسبه نمی‌شود."}
        </p>
      </CardContent>
    </Card>
  );
}
